// client/src/pages/LogsPage.jsx
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import LogForm from '../components/Logs/LogForm';
import LogStatusDialog from '../components/Logs/LogStatusDialog';
import LogHistoryDialog from '../components/Logs/LogHistoryDialog';
import CreateWorkOrderFromLog from '../components/Logs/CreateWorkOrderFromLog';
import axios from '../api/axiosConfig';

// API functions
const fetchLogs = async () => {
  const response = await axios.get('/logs');
  return response.data;
};

const fetchClients = async () => {
  const response = await axios.get('/clients');
  return response.data;
};

const fetchVehicles = async () => {
  const response = await axios.get('/vehicles');
  return response.data;
};

const fetchDevices = async () => {
  const response = await axios.get('/devices');
  return response.data;
};

const fetchWorkOrders = async () => {
  const response = await axios.get('/workorders');
  return response.data;
};

const deleteLog = async (id) => {
  await axios.delete(`/logs/${id}`);
};

const updateLog = async ({ id, data }) => {
  const response = await axios.put(`/logs/${id}`, data);
  return response.data;
};

const LogsPage = () => {
  const [openForm, setOpenForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [statusDialog, setStatusDialog] = useState({ open: false, log: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, log: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [workOrderDialog, setWorkOrderDialog] = useState({ open: false, log: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters state
  const [searchText, setSearchText] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const queryClient = useQueryClient();

  // Queries
  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['logs'],
    queryFn: fetchLogs,
  });

  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
  });

  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
  });

  const { data: workOrders, isLoading: workOrdersLoading } = useQuery({
    queryKey: ['workorders'],
    queryFn: fetchWorkOrders,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteLog,
    onSuccess: () => {
      queryClient.invalidateQueries(['logs']);
      setSnackbar({ open: true, message: 'Log entry deleted', severity: 'success' });
      setDeleteDialog({ open: false, id: null });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Delete failed', severity: 'error' });
    },
  });

  // Update log mutation (for linking work order)
  const updateLogMutation = useMutation({
    mutationFn: updateLog,
    onSuccess: () => {
      queryClient.invalidateQueries(['logs']);
    },
    onError: (err) => {
      console.error('Error updating log:', err);
    },
  });

  // Filter logs
  const filteredLogs = useMemo(() => {
    if (!logs) return [];

    return logs.filter((log) => {
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const titleMatch = log.title?.toLowerCase().includes(searchLower);
        const descMatch = log.description?.toLowerCase().includes(searchLower);
        if (!titleMatch && !descMatch) return false;
      }

      if (selectedClient && log.clientId?._id !== selectedClient._id) return false;
      if (selectedVehicle && log.vehicleId?._id !== selectedVehicle._id) return false;
      if (selectedDevice && log.deviceId?._id !== selectedDevice._id) return false;
      if (selectedWorkOrder && log.workOrderId?._id !== selectedWorkOrder._id) return false;
      if (statusFilter && log.status !== statusFilter) return false;

      return true;
    });
  }, [logs, searchText, selectedClient, selectedVehicle, selectedDevice, selectedWorkOrder, statusFilter]);

  // Clear all filters
  const clearAllFilters = () => {
    setSearchText('');
    setSelectedClient(null);
    setSelectedVehicle(null);
    setSelectedDevice(null);
    setSelectedWorkOrder(null);
    setStatusFilter('');
  };

  const hasActiveFilters =
    searchText ||
    selectedClient ||
    selectedVehicle ||
    selectedDevice ||
    selectedWorkOrder ||
    statusFilter;

  const getStatusColor = (status) => {
    const colors = {
      new: 'info',
      'in-progress': 'warning',
      'waiting-client': 'secondary',
      completed: 'success',
    };
    return colors[status] || 'default';
  };

  const handleWorkOrderCreated = (newWorkOrder, log) => {
    // Обновляем лог, привязывая созданный Work Order
    updateLogMutation.mutate({
      id: log._id,
      data: { workOrderId: newWorkOrder._id }
    });
    setSnackbar({ open: true, message: 'Work Order created and linked to log', severity: 'success' });
  };

  const isLoading = logsLoading || clientsLoading || vehiclesLoading || devicesLoading || workOrdersLoading;

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Journal / Logs
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Track all activities, issues, and communications
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingLog(null);
              setOpenForm(true);
            }}
          >
            Add Log Entry
          </Button>
        </Box>

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Search by title or description"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter keywords to search in title and description..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clients || []}
                getOptionLabel={(option) => option.name}
                value={selectedClient}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="Search by client name..." />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={vehicles || []}
                getOptionLabel={(option) => `${option.licensePlate} - ${option.brand} ${option.model}`}
                value={selectedVehicle}
                onChange={(event, newValue) => setSelectedVehicle(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Vehicle" placeholder="Search by license plate..." />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={devices || []}
                getOptionLabel={(option) => `${option.imei} (${option.type})`}
                value={selectedDevice}
                onChange={(event, newValue) => setSelectedDevice(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Device" placeholder="Search by IMEI..." />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={workOrders || []}
                getOptionLabel={(option) => `${option.title} (${option.status})`}
                value={selectedWorkOrder}
                onChange={(event, newValue) => setSelectedWorkOrder(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Work Order" placeholder="Search by title..." />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="waiting-client">Waiting Client</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
                startIcon={<ClearIcon />}
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Found {filteredLogs.length} log entry(ies)
              {hasActiveFilters && ` (filtered from ${logs?.length || 0} total)`}
            </Typography>
          </Box>
        </Paper>

        {/* Logs Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Vehicle</strong></TableCell>
                <TableCell><strong>Device</strong></TableCell>
                <TableCell><strong>Work Order</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell>{log.title}</TableCell>
                  <TableCell>{log.clientId?.name || '-'}</TableCell>
                  <TableCell>{log.vehicleId?.licensePlate || '-'}</TableCell>
                  <TableCell>{log.deviceId?.imei || '-'}</TableCell>
                  <TableCell>
                    {log.workOrderId ? (
                      <Chip
                        label={log.workOrderId.title}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.status}
                      color={getStatusColor(log.status)}
                      size="small"
                      onClick={() => setStatusDialog({ open: true, log })}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingLog(log);
                          setOpenForm(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Create Work Order">
                      <IconButton
                        size="small"
                        onClick={() => setWorkOrderDialog({ open: true, log })}
                        color="primary"
                      >
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, id: log._id })}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="History">
                      <IconButton
                        size="small"
                        onClick={() => setHistoryDialog({ open: true, log })}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No log entries found matching your criteria</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Log Form Modal */}
        <LogForm
          open={openForm}
          onClose={() => {
            setOpenForm(false);
            setEditingLog(null);
          }}
          onCancel={() => {
            setOpenForm(false);
            setEditingLog(null);
          }}
          log={editingLog}
          onSuccess={() => {
            queryClient.invalidateQueries(['logs']);
            setSnackbar({ open: true, message: 'Log saved successfully', severity: 'success' });
          }}
        />

        {/* Create Work Order from Log Modal */}
        <CreateWorkOrderFromLog
          open={workOrderDialog.open}
          log={workOrderDialog.log}
          onClose={() => setWorkOrderDialog({ open: false, log: null })}
          onSuccess={(newWorkOrder) => {
            handleWorkOrderCreated(newWorkOrder, workOrderDialog.log);
            setWorkOrderDialog({ open: false, log: null });
          }}
        />

        {/* Status Change Dialog */}
        <LogStatusDialog
          open={statusDialog.open}
          log={statusDialog.log}
          onClose={() => setStatusDialog({ open: false, log: null })}
          onSuccess={() => {
            queryClient.invalidateQueries(['logs']);
            setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
          }}
        />

        {/* History Dialog */}
        <LogHistoryDialog
          open={historyDialog.open}
          log={historyDialog.log}
          onClose={() => setHistoryDialog({ open: false, log: null })}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this log entry?</Typography>
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
            <Button onClick={() => deleteMutation.mutate(deleteDialog.id)} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default LogsPage;